import { useState, useEffect } from "react";
import { useI18n, withLocalePrefix } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { AlertTriangle, Loader2, Home } from "lucide-react";
import { Footer } from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";

export default function ConfirmDeletePage() {
    const { t, locale } = useI18n();
    const { toast } = useToast();
    const { signOut } = useAuth();
    const [, setLocation] = useLocation();
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get token from URL
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");

    const handleConfirmDelete = async () => {
        if (!token) return;

        setIsDeleting(true);
        setError(null);

        try {
            const resp = await fetch("/api/users/confirm-delete-account", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ token })
            });

            const data = await resp.json();

            if (resp.ok) {
                toast({
                    title: t("account.confirmDeleteSuccess"),
                    variant: "default"
                });
                // Sign out and redirect
                signOut();
                setLocation(withLocalePrefix("/account-deleted", locale));
            } else {
                setError(data.error || t("account.confirmDeleteError"));
            }
        } catch (err: any) {
            console.error("[Account Delete Confirm]", err);
            setError(t("account.confirmDeleteError"));
        } finally {
            setIsDeleting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <main className="flex-1 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-card p-8 rounded-lg shadow-lg border border-border text-center">
                        <h1 className="text-2xl font-bold mb-4">{t("account.invalidLink")}</h1>
                        <p className="text-muted-foreground mb-6">{t("account.invalidLinkDesc")}</p>
                        <Button onClick={() => setLocation(withLocalePrefix("/", locale))}>{t("account.backToHome")}</Button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <main className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-card p-8 rounded-lg shadow-lg border border-border text-center animate-in fade-in zoom-in duration-500">
                    <div className="mx-auto w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                        <AlertTriangle className="w-12 h-12 text-destructive" />
                    </div>

                    <h1 className="text-3xl font-bold mb-4">{t("account.confirmDeleteTitle")}</h1>
                    <p className="text-muted-foreground mb-8 text-lg">
                        {t("account.confirmDeleteDesc")}
                    </p>

                    {error && (
                        <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-md text-sm font-medium">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <Button
                            variant="destructive"
                            className="w-full gap-2 h-12 text-lg"
                            onClick={handleConfirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                t("account.confirmDeleteButton")
                            )}
                        </Button>

                        <Button
                            variant="ghost"
                            className="w-full gap-2"
                            onClick={() => setLocation(withLocalePrefix("/", locale))}
                            disabled={isDeleting}
                        >
                            {t("common.cancel")}
                        </Button>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
